import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import api, { TOKEN_KEY, REFRESH_KEY, COMPANY_KEY } from '../lib/api.js'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/profile')
      setUser(data?.user || data || null)
      return data
    } catch {
      setUser(null)
      return null
    }
  }, [])

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
      setLoading(false)
      return
    }
    fetchProfile().finally(() => setLoading(false))
  }, [fetchProfile])

  const login = useCallback(
    async (email, password) => {
      // The login endpoint returns 403 ONBOARDING_INCOMPLETE for users whose
      // registration isn't finished — but the body still carries the tokens and
      // the onboarding step. Treat that as a (partial) success so the caller can
      // route to the waiting screen / resume the wizard instead of erroring.
      let data
      try {
        ({ data } = await api.post('/auth/login', { email, password }))
      } catch (err) {
        data = err?.response?.data
        if (!data?.access_token) throw err // genuine failure (bad credentials, …)
      }
      if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token)
      if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token)
      if (data.onboarding?.company_id) localStorage.setItem(COMPANY_KEY, String(data.onboarding.company_id))

      if (data.error === 'ONBOARDING_INCOMPLETE' || data.onboarding) {
        return { incomplete: true, onboarding: data.onboarding || {} }
      }
      // The login `user` is slim (no accepted_terms_at / preferred_language) —
      // always fetch the full profile so the terms gate & language work.
      const profile = await fetchProfile()
      const fullUser = profile?.user || profile
      // Pin the active company BEFORE landing on the dashboard so the very first
      // company-scoped request already carries X-Company-Id. Otherwise the active
      // company is only set by a later effect and the dashboard fetches with no
      // header → empty data until a manual refresh.
      if (!localStorage.getItem(COMPANY_KEY) && fullUser?.companies?.length) {
        localStorage.setItem(COMPANY_KEY, String(fullUser.companies[0].id))
      }
      return { incomplete: false, user: fullUser }
    },
    [fetchProfile],
  )

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(COMPANY_KEY)
    setUser(null)
  }, [])

  const value = { user, setUser, loading, login, logout, fetchProfile, isAuthenticated: !!user }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
